/**
 * Cp-vs-x/c plot for the high-lift foil — the surface-pressure companion to `PolarPlot`.
 *
 * Plots Cp (y inverted, suction up) against x/c for every element in `surfaceCp`, upper and lower
 * surfaces as separate traces. When a `target` is supplied it overlays the target Cp curve for the
 * named element and lets the user RESHAPE it by dragging its points, plus drag an [x0,x1] mixed-inverse
 * region band — both emitted back through `onTargetChange` / `onRegionChange`.
 *
 * Pure presentational SVG (no solver, no module state): all geometry comes from props, all edits are
 * reported via callbacks for the caller (`useFoilDesign` / `InverseDesignPanel`) to own the state.
 * Themed via `useHighliftTheme()` so it follows Thread's light/dark toggle exactly like `PolarPlot`.
 */
import { useMemo, useRef } from "react";

import type { SurfaceCpElement } from "./FlowVisLayer";
import { useHighliftTheme, type HighliftPalette } from "./useHighliftTheme";

const FONT_STACK = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** The editable target-Cp curve for one element (either side optional). */
export interface CpTarget {
  element: string;
  upper?: [number, number][];
  lower?: [number, number][];
}

export interface CpPlotProps {
  /** Solved surface-Cp strips, one per element (honest-absent → empty array). */
  surfaceCp: SurfaceCpElement[];
  /** Optional target-Cp overlay for one element; its points are draggable when `onTargetChange` is set. */
  target?: CpTarget;
  /** Fired with the reshaped target after a point drag. */
  onTargetChange?: (t: CpTarget) => void;
  /** Optional [x0,x1] mixed-inverse region band; draggable when `onRegionChange` is set. */
  region?: [number, number];
  /** Fired with the new [x0,x1] after a band/edge drag (always sorted, clamped to the x domain). */
  onRegionChange?: (region: [number, number]) => void;
  width?: number;
  height?: number;
}

const MARGIN = { top: 18, right: 16, bottom: 36, left: 48 } as const;

type Side = "upper" | "lower";

/** Active drag: a single target point, or a region edge / whole band. */
type Drag =
  | { kind: "point"; side: Side; index: number }
  | { kind: "region"; mode: "x0" | "x1" | "band"; grabX: number; x0: number; x1: number };

/** Per-element trace colour cycle (geometry edge hues, so a trace matches its element on the canvas). */
function elementColors(p: HighliftPalette): string[] {
  return [p.mainEdge, p.vaneEdge, p.flapEdge, p.slatEdge, p.kruegerEdge, p.blue];
}

/** A linear domain [lo,hi] padded by `pad` of its span (min span guarded). */
function padded(lo: number, hi: number, pad: number): [number, number] {
  const span = Math.max(hi - lo, 1e-6);
  return [lo - span * pad, hi + span * pad];
}

export function CpPlot({
  surfaceCp,
  target,
  onTargetChange,
  region,
  onRegionChange,
  width = 560,
  height = 320,
}: CpPlotProps) {
  const palette = useHighliftTheme();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<Drag | null>(null);

  const plotW = width - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;

  // ---- domains (over solved + target points; Cp axis inverted so suction is up) ----
  const { xDom, cpDom } = useMemo(() => {
    let xLo = 0;
    let xHi = 1;
    let cpLo = Infinity;
    let cpHi = -Infinity;
    const eat = (pts: [number, number][] | undefined): void => {
      if (!pts) return;
      for (const [x, cp] of pts) {
        if (!Number.isFinite(x) || !Number.isFinite(cp)) continue;
        xLo = Math.min(xLo, x);
        xHi = Math.max(xHi, x);
        cpLo = Math.min(cpLo, cp);
        cpHi = Math.max(cpHi, cp);
      }
    };
    for (const el of surfaceCp) {
      eat(el.upper);
      eat(el.lower);
    }
    eat(target?.upper);
    eat(target?.lower);
    if (!Number.isFinite(cpLo) || !Number.isFinite(cpHi)) {
      cpLo = -1;
      cpHi = 1;
    }
    return { xDom: padded(xLo, xHi, 0.02), cpDom: padded(cpLo, cpHi, 0.08) };
  }, [surfaceCp, target]);

  // ---- scales (data ↔ viewBox px). Cp is inverted: most-negative Cp at the TOP. ----
  const scales = useMemo(() => {
    const [x0, x1] = xDom;
    const [cMin, cMax] = cpDom;
    const xToPx = (x: number): number => MARGIN.left + ((x - x0) / (x1 - x0)) * plotW;
    // cMin (most negative / suction) maps to the top of the plot.
    const cpToPy = (cp: number): number => MARGIN.top + ((cp - cMin) / (cMax - cMin)) * plotH;
    const pxToX = (px: number): number => x0 + ((px - MARGIN.left) / plotW) * (x1 - x0);
    const pyToCp = (py: number): number => cMin + ((py - MARGIN.top) / plotH) * (cMax - cMin);
    return { xToPx, cpToPy, pxToX, pyToCp };
  }, [xDom, cpDom, plotW, plotH]);

  const { xToPx, cpToPy, pxToX, pyToCp } = scales;

  /** Map a pointer event to viewBox (data-space) coordinates, accounting for SVG scaling. */
  const eventToVb = (clientX: number, clientY: number): { px: number; py: number } => {
    const svg = svgRef.current;
    if (!svg) return { px: 0, py: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      px: ((clientX - rect.left) / rect.width) * width,
      py: ((clientY - rect.top) / rect.height) * height,
    };
  };

  // ---- drag lifecycle ----------------------------------------------------------------
  const beginDrag = (e: React.PointerEvent, drag: Drag): void => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = drag;
    svgRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const drag = dragRef.current;
    if (!drag) return;
    const { px, py } = eventToVb(e.clientX, e.clientY);
    const [xLo, xHi] = xDom;
    const clampX = (x: number): number => Math.max(xLo, Math.min(xHi, x));

    if (drag.kind === "point") {
      if (!onTargetChange || !target) return;
      const src = (drag.side === "upper" ? target.upper : target.lower) ?? [];
      const next = src.map((p) => [p[0], p[1]] as [number, number]);
      const lo = drag.index > 0 ? next[drag.index - 1]![0] : xLo;
      const hi = drag.index < next.length - 1 ? next[drag.index + 1]![0] : xHi;
      // Keep x monotonic (stay between neighbours); Cp follows the cursor freely.
      next[drag.index] = [Math.max(lo, Math.min(hi, pxToX(px))), pyToCp(py)];
      onTargetChange(
        drag.side === "upper" ? { ...target, upper: next } : { ...target, lower: next },
      );
      return;
    }

    // region drag
    if (!onRegionChange) return;
    if (drag.mode === "band") {
      const w = drag.x1 - drag.x0;
      let nx0 = clampX(drag.x0 + (pxToX(px) - drag.grabX));
      let nx1 = nx0 + w;
      if (nx1 > xHi) {
        nx1 = xHi;
        nx0 = xHi - w;
      }
      if (nx0 < xLo) {
        nx0 = xLo;
        nx1 = xLo + w;
      }
      onRegionChange([nx0, nx1]);
      return;
    }
    const x = clampX(pxToX(px));
    const pair: [number, number] = drag.mode === "x0" ? [x, drag.x1] : [drag.x0, x];
    onRegionChange([Math.min(pair[0], pair[1]), Math.max(pair[0], pair[1])]);
  };

  const endDrag = (e: React.PointerEvent): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      svgRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* capture may already be gone */
    }
  };

  // ---- ticks -------------------------------------------------------------------------
  const xTicks = useMemo(() => niceTicks(xDom[0], xDom[1], 6), [xDom]);
  const cpTicks = useMemo(() => niceTicks(cpDom[0], cpDom[1], 5), [cpDom]);

  const colors = elementColors(palette);

  // Build a polyline path from data points.
  const pathOf = (pts: [number, number][]): string =>
    pts
      .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]))
      .map((p, i) => `${i === 0 ? "M" : "L"}${xToPx(p[0]).toFixed(2)} ${cpToPy(p[1]).toFixed(2)}`)
      .join(" ");

  const editable = Boolean(target && onTargetChange);
  const regionEditable = Boolean(region && onRegionChange);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      style={{ display: "block", touchAction: "none", fontFamily: FONT_STACK, userSelect: "none" }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <rect
        x={MARGIN.left}
        y={MARGIN.top}
        width={plotW}
        height={plotH}
        fill={palette.surface}
        stroke={palette.line}
      />

      {/* region band (mixed-inverse [x0,x1]) — under the traces */}
      {region && (
        <g>
          <rect
            x={xToPx(region[0])}
            y={MARGIN.top}
            width={Math.max(0, xToPx(region[1]) - xToPx(region[0]))}
            height={plotH}
            fill={palette.primarySoft}
            opacity={0.55}
            style={{ cursor: regionEditable ? "grab" : "default" }}
            onPointerDown={
              regionEditable
                ? (e) => {
                    const { px } = eventToVb(e.clientX, e.clientY);
                    beginDrag(e, {
                      kind: "region",
                      mode: "band",
                      grabX: pxToX(px),
                      x0: region[0],
                      x1: region[1],
                    });
                  }
                : undefined
            }
          />
          {(["x0", "x1"] as const).map((edge, i) => {
            const bound = i === 0 ? region[0] : region[1];
            const ex = xToPx(bound);
            return (
              <line
                key={edge}
                x1={ex}
                y1={MARGIN.top}
                x2={ex}
                y2={MARGIN.top + plotH}
                stroke={palette.primary}
                strokeWidth={regionEditable ? 6 : 1.5}
                strokeOpacity={regionEditable ? 0.9 : 1}
                style={{ cursor: regionEditable ? "ew-resize" : "default" }}
                onPointerDown={
                  regionEditable
                    ? (e) =>
                        beginDrag(e, {
                          kind: "region",
                          mode: edge,
                          grabX: bound,
                          x0: region[0],
                          x1: region[1],
                        })
                    : undefined
                }
              />
            );
          })}
        </g>
      )}

      {/* grid + ticks */}
      <g>
        {xTicks.map((t) => (
          <g key={`x${t}`}>
            <line
              x1={xToPx(t)}
              y1={MARGIN.top}
              x2={xToPx(t)}
              y2={MARGIN.top + plotH}
              stroke={palette.line}
              strokeWidth={0.5}
            />
            <text
              x={xToPx(t)}
              y={MARGIN.top + plotH + 14}
              fill={palette.ink3}
              fontSize={10}
              fontFamily={MONO_STACK}
              textAnchor="middle"
            >
              {t.toFixed(2)}
            </text>
          </g>
        ))}
        {cpTicks.map((t) => (
          <g key={`c${t}`}>
            <line
              x1={MARGIN.left}
              y1={cpToPy(t)}
              x2={MARGIN.left + plotW}
              y2={cpToPy(t)}
              stroke={Math.abs(t) < 1e-9 ? palette.lineStrong : palette.line}
              strokeWidth={Math.abs(t) < 1e-9 ? 1 : 0.5}
            />
            <text
              x={MARGIN.left - 6}
              y={cpToPy(t) + 3}
              fill={palette.ink3}
              fontSize={10}
              fontFamily={MONO_STACK}
              textAnchor="end"
            >
              {t.toFixed(1)}
            </text>
          </g>
        ))}
      </g>

      {/* axis titles */}
      <text
        x={MARGIN.left + plotW / 2}
        y={height - 4}
        fill={palette.ink3}
        fontSize={11}
        textAnchor="middle"
      >
        x/c
      </text>
      <text
        x={12}
        y={MARGIN.top + plotH / 2}
        fill={palette.ink3}
        fontSize={11}
        textAnchor="middle"
        transform={`rotate(-90 12 ${MARGIN.top + plotH / 2})`}
      >
        C_p (suction up)
      </text>

      {/* solved traces — one colour per element, upper solid / lower dashed */}
      {surfaceCp.map((el, ei) => {
        const color = colors[ei % colors.length]!;
        return (
          <g key={el.element}>
            {el.upper.length > 0 && (
              <path d={pathOf(el.upper)} fill="none" stroke={color} strokeWidth={1.75} />
            )}
            {el.lower.length > 0 && (
              <path
                d={pathOf(el.lower)}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                opacity={0.85}
              />
            )}
          </g>
        );
      })}

      {/* target overlay — dashed amber curve + draggable points */}
      {target && (
        <g>
          {(["upper", "lower"] as const).map((side) => {
            const pts = (side === "upper" ? target.upper : target.lower) ?? [];
            if (pts.length === 0) return null;
            return (
              <g key={side}>
                <path
                  d={pathOf(pts)}
                  fill="none"
                  stroke={palette.amber}
                  strokeWidth={1.75}
                  strokeDasharray={side === "lower" ? "5 3" : undefined}
                />
                {editable &&
                  pts.map((p, i) => (
                    <circle
                      key={`${side}-${i}`}
                      cx={xToPx(p[0])}
                      cy={cpToPy(p[1])}
                      r={5}
                      fill={palette.surface}
                      stroke={palette.amber}
                      strokeWidth={1.75}
                      style={{ cursor: "grab" }}
                      onPointerDown={(e) => beginDrag(e, { kind: "point", side, index: i })}
                    />
                  ))}
              </g>
            );
          })}
        </g>
      )}
    </svg>
  );
}

/** Round, evenly-spaced ticks across [lo,hi] (≈`count` of them). Pure. */
function niceTicks(lo: number, hi: number, count: number): number[] {
  const span = hi - lo;
  if (!(span > 0) || !Number.isFinite(span)) return [lo];
  const raw = span / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const start = Math.ceil(lo / step) * step;
  const out: number[] = [];
  for (let t = start; t <= hi + step * 1e-6; t += step) out.push(Math.abs(t) < step * 1e-6 ? 0 : t);
  return out;
}
